import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useAppStore } from '@/store/appStore'
import { apiFetch, fetchScanPolicy, updateScanPolicyFlags, ScanPolicyFlags } from '@/utils/api'
import { RunningScansPanel, RunningScan } from '../components/admin/RunningScansPanel';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar
} from 'recharts'

interface Metrics {
  users: { total: number; admins: number }
  scans: { last7d: { started: number; completed: number }; last30d: { started: number; completed: number } }
  timeToFirstReportMsAvg7d: number | null
  verifications7d: number
  falsePositives7d: number
  visits: { series: Array<{ day: string; visits: number }>; topPages: Array<{ path: string; c: number }> }
}

interface VisitorMetrics {
  windowDays: number
  daily: Array<{
    day: string
    visits: number
    uniqueVisitors: number
    repeatVisitors: number
    adminVisits: number
  }>
  totals: {
    visits: number
    uniqueVisitors: number
    adminVisits: number
    returningVisitors7d: number
    avgVisitsPerVisitor: number
  }
}

interface ScanRequestMetrics {
  windowDays: number
  daily: Array<{ day: string; accepted: number; blocked: number; errors: number }>
  totals: {
    accepted: number
    blocked: number
    errors: number
    httpEvents: number
    wsEvents: number
    transportBreakdown: Record<string, { accepted: number; blocked: number; error: number }>
    topBlockReasons: Array<{ reason: string; count: number }>
  }
}

interface FunnelMetrics {
  windowDays: number
  totals: { requested: number; verified: number; failed: number }
  daily: Array<{ day: string; requested: number; verified: number; failed: number }>
}

interface TopPagesMetrics {
  windowDays: number
  totalVisits: number
  items: Array<{ path: string; count: number; share: number }>
}

interface PrecisionTrendEntry {
  day: string
  harnessRuns: number
  payloadBatches: number
  evasions: number
  harnessMs: number | null
  payloadMs: number | null
}

interface PrecisionMetrics {
  windowDays: number
  coverage: {
    harnessedParameters: number
    discoveredParameters: number
    percent: number | null
  }
  harness: {
    totalRuns: number
    succeededRuns: number
    averageDurationMs: number | null
    reflectionsDetected: number
    payloadAttempts: number
  }
  adoption: {
    prescanEvents: number
    harnessUsed: number
    payloadUsed: number
    harnessUsageRate: number | null
    payloadUsageRate: number | null
  }
  payloadDiversity: {
    batches: number
    totalVariants: number
    uniqueSignatures: number
    avgVariantsPerBatch: number | null
    avgSignaturesPerBatch: number | null
  }
  evasions: {
    total: number
    parametersImpacted: number
    wafIndicatorsCleared: number
    successRate: number | null
  }
  latency: {
    paramHarnessMs: number | null
    payloadLabMs: number | null
    sqlmapMs: number | null
    queueLatencyMs: number | null
  }
  trend: PrecisionTrendEntry[]
}

type ScanPolicyToggleKey = keyof ScanPolicyFlags

const POLICY_TOGGLE_META: Record<ScanPolicyToggleKey, {
  title: string
  description: string
  helper: string
  confirmOn: string
  confirmOff: string
  tone?: 'danger' | 'warning'
}> = {
  allowArbitraryVerification: {
    title: 'Allow Arbitrary Verification',
    description: 'Permit members to request verification tokens for any hostname they control.',
    helper: 'Disable to keep verification requests confined to the curated safe-target list.',
    confirmOn: 'Allow arbitrary verification requests again?',
    confirmOff: 'Disable arbitrary verification and require preapproved hosts?',
    tone: 'warning',
  },
  enforcePreapprovedTargets: {
    title: 'Enforce Preapproved Targets',
    description: 'Require all non-admin scans to point at the sanctioned safe-host inventory.',
    helper: 'When enabled, verified customer domains are ignored unless they are preapproved.',
    confirmOn: 'Enforce the preapproved safe-target list for all non-admin scans?',
    confirmOff: 'Allow verified domains outside the preapproved list?',
    tone: 'warning',
  },
  disableNonAdminScans: {
    title: 'Disable Non-Admin Scans',
    description: 'Immediate kill switch that blocks new scans from non-admin accounts.',
    helper: 'Running scans keep going, but no new jobs start until you re-enable access.',
    confirmOn: 'Enable the non-admin kill switch? Active scans keep running.',
    confirmOff: 'Re-enable non-admin scanning?',
    tone: 'danger',
  }
}

const formatPolicyTimestamp = (value: string | null) => {
  if (!value) return 'Never updated'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value || 'Never updated'
  }
}

const VISITOR_WINDOWS = [7, 14, 30] as const
const SCAN_WINDOWS = [7, 14, 30] as const
const FUNNEL_WINDOWS = [7, 14, 30] as const
const TOP_PAGE_WINDOWS = [7, 14, 30] as const
const PRECISION_WINDOWS = [7, 14, 30] as const
const PRECISION_THRESHOLDS = {
  coverage: { warn: 85, danger: 70 },
  harnessUsage: { warn: 80, danger: 55 },
  payloadUsage: { warn: 55, danger: 30 },
  latency: { warn: 1500, danger: 2500 }
}

type StatusTone = 'success' | 'warning' | 'danger' | 'muted'

const STATUS_BADGE_STYLES: Record<StatusTone, { label: string; className: string }> = {
  success: { label: 'Healthy', className: 'text-emerald-200 bg-emerald-500/10 border border-emerald-400/30' },
  warning: { label: 'Monitor', className: 'text-amber-200 bg-amber-500/10 border border-amber-400/30' },
  danger: { label: 'Action', className: 'text-red-200 bg-red-500/10 border border-red-400/30' },
  muted: { label: 'n/a', className: 'text-gray-300 bg-gray-700/50 border border-gray-600/80' }
}

const ALERT_STYLES: Record<'warning' | 'danger', { container: string; accent: string }> = {
  warning: {
    container: 'border-amber-500/40 bg-amber-500/10 text-amber-100',
    accent: 'text-amber-200'
  },
  danger: {
    container: 'border-red-500/50 bg-red-500/10 text-red-100',
    accent: 'text-red-200'
  }
}

const evaluateMetricTone = (
  value: number | null | undefined,
  { warn, danger }: { warn: number; danger: number },
  { higherIsBetter = true }: { higherIsBetter?: boolean } = {}
): StatusTone => {
  if (value == null || Number.isNaN(value)) return 'muted'
  if (higherIsBetter) {
    if (value < danger) return 'danger'
    if (value < warn) return 'warning'
    return 'success'
  }
  if (value > danger) return 'danger'
  if (value > warn) return 'warning'
  return 'success'
}

const formatNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return '-'
  if (value >= 1000) {
    const shortened = (value / 1000).toFixed(1)
    return shortened + 'k'
  }
  return value.toLocaleString()
}

export default function Admin() {
  const currentUser = useAppStore(s => s.currentUser)
  const scanPolicy = useAppStore(s => s.scanPolicy)
  const setGlobalScanPolicy = useAppStore(s => s.setScanPolicy)
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [settings, setSettings] = useState<{
    require_proxy: { effective: boolean; env: string | null; db: string | null }
    trust_proxy: { effective: string; env: string | null; db: string | null }
  } | null>(null)
  const [saving, setSaving] = useState(false)
  const [visitorMetrics, setVisitorMetrics] = useState<VisitorMetrics | null>(null)
  const [scanMetrics, setScanMetrics] = useState<ScanRequestMetrics | null>(null)
  const [funnelMetrics, setFunnelMetrics] = useState<FunnelMetrics | null>(null)
  const [topPagesMetrics, setTopPagesMetrics] = useState<TopPagesMetrics | null>(null)
  const [visitorWindow, setVisitorWindow] = useState<number>(30)
  const [scanWindow, setScanWindow] = useState<number>(30)
  const [funnelWindow, setFunnelWindow] = useState<number>(30)
  const [topPagesWindow, setTopPagesWindow] = useState<number>(14)
  const [precisionMetrics, setPrecisionMetrics] = useState<PrecisionMetrics | null>(null)
  const [precisionWindow, setPrecisionWindow] = useState<number>(14)
  const [visitorLoading, setVisitorLoading] = useState(false)
  const [scanLoading, setScanLoading] = useState(false)
  const [funnelLoading, setFunnelLoading] = useState(false)
  const [topPagesLoading, setTopPagesLoading] = useState(false)
  const [precisionLoading, setPrecisionLoading] = useState(false)
  const [scanPolicyLoading, setScanPolicyLoading] = useState(true)
  const [scanPolicySavingKey, setScanPolicySavingKey] = useState<ScanPolicyToggleKey | null>(null)

  const handleTerminateScan = useCallback(async (scan: RunningScan) => {
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm('Terminate scan ' + scan.scanId + '?')
      if (!confirmed) return
    }

    let reason: string | undefined
    if (typeof window !== 'undefined') {
      const input = window.prompt('Reason for termination (optional)', 'Terminated by administrator')
      if (input && input.trim().length > 0) {
        reason = input.trim()
      }
    }

    try {
      await apiFetch('/api/admin/scans/' + encodeURIComponent(scan.scanId) + '/terminate', {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {})
      })
      toast.success('Termination requested for ' + scan.scanId)
    } catch (e: any) {
      toast.error(e?.message || 'Failed to terminate scan')
      throw e
    }
  }, [scanPolicy])

  const loadScanPolicy = useCallback(async () => {
    setScanPolicyLoading(true)
    try {
      const policy = await fetchScanPolicy()
      setGlobalScanPolicy(policy, { metaIncluded: true })
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load scan policy')
    } finally {
      setScanPolicyLoading(false)
    }
  }, [setGlobalScanPolicy])

  const handleScanPolicyToggle = useCallback(async (key: ScanPolicyToggleKey, nextValue: boolean) => {
    if (!scanPolicy) return
    const meta = POLICY_TOGGLE_META[key]
    const confirmMessage = nextValue ? meta.confirmOn : meta.confirmOff
    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(confirmMessage)
      if (!confirmed) return
    }
    setScanPolicySavingKey(key)
    try {
      const updated = await updateScanPolicyFlags({ [key]: nextValue })
      setGlobalScanPolicy(updated, { metaIncluded: true })
      toast.success(`${meta.title} ${nextValue ? 'enabled' : 'disabled'}`)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update scan policy')
    } finally {
      setScanPolicySavingKey(null)
    }
  }, [scanPolicy, setGlobalScanPolicy])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [m, s] = await Promise.all([
          apiFetch<Metrics>('/api/admin/metrics'),
          apiFetch<{ settings: any }>('/api/admin/settings')
        ])
        setMetrics(m)
        setSettings(s.settings)
      } catch (e: any) {
        setError(e.message || 'Failed to fetch metrics')
        toast.error(e.message || 'Failed to fetch metrics')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  useEffect(() => {
    const loadVisitors = async () => {
      try {
        setVisitorLoading(true)
        const data = await apiFetch<VisitorMetrics>(`/api/admin/metrics/visitors?days=${visitorWindow}`)
        setVisitorMetrics(data)
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load visitor analytics')
      } finally {
        setVisitorLoading(false)
      }
    }
    loadVisitors()
  }, [visitorWindow])

  useEffect(() => {
    const loadScanMetrics = async () => {
      try {
        setScanLoading(true)
        const data = await apiFetch<ScanRequestMetrics>(`/api/admin/metrics/scans?days=${scanWindow}`)
        setScanMetrics(data)
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load scan analytics')
      } finally {
        setScanLoading(false)
      }
    }
    loadScanMetrics()
  }, [scanWindow])

  useEffect(() => {
    const loadFunnelMetrics = async () => {
      try {
        setFunnelLoading(true)
        const data = await apiFetch<FunnelMetrics>(`/api/admin/metrics/funnel?days=${funnelWindow}`)
        setFunnelMetrics(data)
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load verification funnel')
      } finally {
        setFunnelLoading(false)
      }
    }
    loadFunnelMetrics()
  }, [funnelWindow])

  useEffect(() => {
    const loadTopPages = async () => {
      try {
        setTopPagesLoading(true)
        const data = await apiFetch<TopPagesMetrics>(`/api/admin/metrics/top-pages?days=${topPagesWindow}`)
        setTopPagesMetrics(data)
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load top pages')
      } finally {
        setTopPagesLoading(false)
      }
    }
    loadTopPages()
  }, [topPagesWindow])

  useEffect(() => {
    loadScanPolicy()
  }, [loadScanPolicy])

  useEffect(() => {
    const loadPrecisionMetrics = async () => {
      try {
        setPrecisionLoading(true)
        const data = await apiFetch<PrecisionMetrics>(`/api/admin/metrics/precision?days=${precisionWindow}`)
        setPrecisionMetrics(data)
      } catch (e: any) {
        toast.error(e?.message || 'Failed to load precision metrics')
      } finally {
        setPrecisionLoading(false)
      }
    }
    loadPrecisionMetrics()
  }, [precisionWindow])

  const visitorCards = useMemo(() => ([
    { label: 'Total visits', value: visitorMetrics?.totals.visits, helper: `${visitorWindow}d window` },
    { label: 'Unique visitors', value: visitorMetrics?.totals.uniqueVisitors },
    { label: 'Returning visitors (7d)', value: visitorMetrics?.totals.returningVisitors7d },
    { label: 'Admin visits', value: visitorMetrics?.totals.adminVisits },
    {
      label: 'Avg visits / visitor',
      value: visitorMetrics ? Number(visitorMetrics.totals.avgVisitsPerVisitor.toFixed(2)) : null
    }
  ]), [visitorMetrics, visitorWindow])

  const scanCards = useMemo(() => ([
    { label: 'Accepted scans', value: scanMetrics?.totals.accepted },
    { label: 'Blocked attempts', value: scanMetrics?.totals.blocked },
    { label: 'Errors', value: scanMetrics?.totals.errors },
    { label: 'HTTP requests', value: scanMetrics?.totals.httpEvents },
    { label: 'WS requests', value: scanMetrics?.totals.wsEvents }
  ]), [scanMetrics])

  const transportBreakdown = useMemo(() => {
    if (!scanMetrics) return []
    return Object.entries(scanMetrics.totals.transportBreakdown).map(([via, stats]) => ({
      via,
      accepted: stats.accepted,
      blocked: stats.blocked,
      errors: stats.error
    }))
  }, [scanMetrics])

  const funnelCards = useMemo(() => {
    const totals = funnelMetrics?.totals
    const conversion = totals && totals.requested > 0
      ? (totals.verified / totals.requested) * 100
      : null
    return [
      { label: 'Verification requests', value: totals?.requested, helper: `${funnelWindow}d window` },
      { label: 'Verified', value: totals?.verified },
      { label: 'Failed / errored', value: totals?.failed },
      {
        label: 'Conversion rate',
        value: conversion != null ? `${conversion.toFixed(1)}%` : null
      }
    ]
  }, [funnelMetrics, funnelWindow])

  const precisionCards = useMemo(() => {
    if (!precisionMetrics) return []
    const coverageTone = evaluateMetricTone(
      precisionMetrics.coverage.percent,
      PRECISION_THRESHOLDS.coverage
    )
    const harnessUsageTone = evaluateMetricTone(
      precisionMetrics.adoption.harnessUsageRate,
      PRECISION_THRESHOLDS.harnessUsage
    )
    const payloadUsageTone = evaluateMetricTone(
      precisionMetrics.adoption.payloadUsageRate,
      PRECISION_THRESHOLDS.payloadUsage
    )
    const latencyTone = evaluateMetricTone(
      precisionMetrics.latency.paramHarnessMs,
      PRECISION_THRESHOLDS.latency,
      { higherIsBetter: false }
    )
    return [
      {
        label: 'Harness coverage',
        value: precisionMetrics.coverage.percent != null ? `${precisionMetrics.coverage.percent.toFixed(1)}%` : null,
        helper: `${precisionMetrics.coverage.harnessedParameters}/${precisionMetrics.coverage.discoveredParameters} parameters`,
        tone: coverageTone
      },
      {
        label: 'Harness adoption',
        value: precisionMetrics.adoption.harnessUsageRate != null ? `${precisionMetrics.adoption.harnessUsageRate.toFixed(1)}%` : null,
        helper: `${precisionMetrics.adoption.harnessUsed}/${precisionMetrics.adoption.prescanEvents} prescan events`,
        tone: harnessUsageTone
      },
      {
        label: 'Payload adoption',
        value: precisionMetrics.adoption.payloadUsageRate != null ? `${precisionMetrics.adoption.payloadUsageRate.toFixed(1)}%` : null,
        helper: `${precisionMetrics.adoption.payloadUsed}/${precisionMetrics.adoption.prescanEvents} prescan events`,
        tone: payloadUsageTone
      },
      {
        label: 'Harness median latency',
        value: precisionMetrics.latency.paramHarnessMs != null ? `${precisionMetrics.latency.paramHarnessMs} ms` : null,
        helper: 'Median time to replay a parameter',
        tone: latencyTone
      }
    ]
  }, [precisionMetrics])

  const precisionAlerts = useMemo(() => {
    if (!precisionMetrics) return []
    const alerts: Array<{ level: 'warning' | 'danger'; title: string; description: string }> = []

    const coverageTone = evaluateMetricTone(
      precisionMetrics.coverage.percent,
      PRECISION_THRESHOLDS.coverage
    )
    if (coverageTone === 'danger') {
      alerts.push({
        level: 'danger',
        title: 'Harness coverage critically low',
        description: `Only ${precisionMetrics.coverage.percent?.toFixed(1) ?? '?'}% of recon parameters were replayed this window. Verify target toggles and recon freshness.`
      })
    } else if (coverageTone === 'warning') {
      alerts.push({
        level: 'warning',
        title: 'Harness coverage drifting',
        description: `Coverage dipped below ${PRECISION_THRESHOLDS.coverage.warn}%—consider bumping recon scope or prioritizing high-scoring parameters.`
      })
    }

    const harnessUsageTone = evaluateMetricTone(
      precisionMetrics.adoption.harnessUsageRate,
      PRECISION_THRESHOLDS.harnessUsage
    )
    if (harnessUsageTone === 'danger') {
      alerts.push({
        level: 'danger',
        title: 'Harness stage disabled in most prescans',
        description: 'Fewer than half of prescan jobs executed the harness stage. Confirm per-target toggles and queue policy flags.'
      })
    }

    const payloadUsageTone = evaluateMetricTone(
      precisionMetrics.adoption.payloadUsageRate,
      PRECISION_THRESHOLDS.payloadUsage
    )
    if (payloadUsageTone === 'danger') {
      alerts.push({
        level: 'warning',
        title: 'Adaptive payload lab rarely triggered',
        description: 'Payload batches ran on only a handful of prescan jobs. Ensure eligible targets have the payload lab flag enabled.'
      })
    }

    const latencyTone = evaluateMetricTone(
      precisionMetrics.latency.paramHarnessMs,
      PRECISION_THRESHOLDS.latency,
      { higherIsBetter: false }
    )
    if (latencyTone === 'danger') {
      alerts.push({
        level: 'warning',
        title: 'Harness latency spiking',
        description: `Median harness replay now exceeds ${PRECISION_THRESHOLDS.latency.danger}ms. Check target responsiveness or reduce parameter limits.`
      })
    }

    return alerts;
  }, [precisionMetrics])

  const precisionLatency = useMemo(() => precisionMetrics ? [
    { label: 'Harness median', value: precisionMetrics.latency.paramHarnessMs },
    { label: 'Payload lab median', value: precisionMetrics.latency.payloadLabMs },
    { label: 'SQLMap median', value: precisionMetrics.latency.sqlmapMs },
    { label: 'Queue latency', value: precisionMetrics.latency.queueLatencyMs }
  ] : [], [precisionMetrics])

  if (currentUser?.role !== 'admin') {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-400">Forbidden</h1>
        <p className="text-gray-300">You need admin privileges to access this page.</p>
      </div>
    )
  }

  if (loading) {
    return <div className="p-6">Loading metrics.</div>
  }
  if (error) {
    return <div className="p-6 text-red-400">{error}</div>
  }
  if (!metrics) {
    return <div className="p-6">No metrics available.</div>
  }

  const avgTtfr = metrics.timeToFirstReportMsAvg7d != null ? `${Math.round(metrics.timeToFirstReportMsAvg7d / 1000)}s` : '-'
  const requireProxyEffective = settings?.require_proxy.effective ? 'Enabled' : 'Disabled'
  const trustProxyEffective = settings?.trust_proxy.effective || 'auto'

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-3xl font-bold text-blue-300 text-balance">Admin Dashboard</h1>

      <section className="rounded-xl border border-gray-800 bg-gray-900/80 p-4 md:p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Scan Governance Controls</h2>
            <p className="text-sm text-gray-400">Flip verification and scan policies without redeploying.</p>
          </div>
          <div className="text-xs text-gray-400 text-right leading-snug">
            {scanPolicyLoading ? (
              <span>Loading policy…</span>
            ) : (
              <>
                <div>{scanPolicy?.updatedAt ? `Updated ${formatPolicyTimestamp(scanPolicy.updatedAt)}` : 'Never updated'}</div>
                {scanPolicy?.updatedBy && <div>By {scanPolicy.updatedBy}</div>}
              </>
            )}
          </div>
        </div>
        {scanPolicyLoading && (
          <div className="rounded-lg border border-dashed border-gray-700 p-4 text-sm text-gray-400">
            Loading scan policy…
          </div>
        )}
        {!scanPolicyLoading && !scanPolicy && (
          <div className="rounded-lg border border-red-500/40 bg-red-950/30 p-4 text-sm text-red-200">
            Unable to load the current scan policy. Refresh or check server logs.
          </div>
        )}
        {!scanPolicyLoading && scanPolicy && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(Object.keys(POLICY_TOGGLE_META) as ScanPolicyToggleKey[]).map(key => {
              const meta = POLICY_TOGGLE_META[key]
              const value = scanPolicy[key]
              const disabled = scanPolicySavingKey === key
              const highlight = meta.tone === 'danger' && value
              return (
                <div
                  key={key}
                  className={`rounded-lg border p-4 transition ${highlight ? 'border-red-500/70 bg-red-950/40' : 'border-gray-700 bg-gray-800/70'
                    }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{meta.title}</p>
                      <p className="text-xs text-gray-400">{meta.description}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={value}
                      aria-label={meta.title}
                      disabled={disabled}
                      onClick={() => handleScanPolicyToggle(key, !value)}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${disabled
                        ? 'cursor-not-allowed bg-gray-600/60 opacity-50'
                        : value
                          ? 'bg-green-500/80'
                          : 'bg-gray-600'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${value ? 'translate-x-5' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className={`font-semibold ${value ? 'text-green-300' : 'text-gray-400'}`}>
                      {value ? 'Enabled' : 'Disabled'}
                    </span>
                    {scanPolicySavingKey === key && <span className="text-blue-300">Updating…</span>}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">{meta.helper}</p>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Users</div>
          <div className="text-3xl font-bold">{metrics.users.total}</div>
          <div className="text-xs text-gray-400">Admins: {metrics.users.admins}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Scans last 7d</div>
          <div className="text-3xl font-bold">{metrics.scans.last7d.started}</div>
          <div className="text-xs text-gray-400">Completed: {metrics.scans.last7d.completed}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Avg time to first report (7d)</div>
          <div className="text-3xl font-bold">{avgTtfr}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Verifications (7d)</div>
          <div className="text-3xl font-bold">{metrics.verifications7d}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">False positives (7d)</div>
          <div className="text-3xl font-bold">{metrics.falsePositives7d}</div>
        </div>
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="text-gray-400 text-sm">Scans last 30d</div>
          <div className="text-3xl font-bold">{metrics.scans.last30d.started}</div>
          <div className="text-xs text-gray-400">Completed: {metrics.scans.last30d.completed}</div>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Visitor analytics</h2>
            <p className="text-sm text-gray-400">Unique vs returning traffic. Automatically refreshed every few minutes.</p>
          </div>
          <div className="flex gap-2">
            {VISITOR_WINDOWS.map(window => (
              <button
                key={window}
                className={`px-3 py-1 rounded-md text-sm border ${visitorWindow === window ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700 hover:text-white'}`}
                onClick={() => setVisitorWindow(window)}
              >
                {window}d
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {visitorCards.map(card => (
            <div key={card.label} className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">{card.label}</p>
              <p className="text-2xl font-semibold text-white mt-2">{formatNumber(card.value as number | null)}</p>
              {card.helper && <p className="text-xs text-gray-500 mt-1">{card.helper}</p>}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-2 rounded-lg border border-gray-700 bg-gray-800/70 p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Visitors timeline</h3>
                <p className="text-sm text-gray-400">Unique vs total visits per day</p>
              </div>
              {visitorLoading && <span className="text-xs text-blue-300">Refreshing…</span>}
            </div>
            {visitorMetrics && visitorMetrics.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={visitorMetrics.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="day" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151' }} />
                  <Legend />
                  <Line type="monotone" dataKey="visits" stroke="#60a5fa" strokeWidth={2} dot={false} name="Visits" />
                  <Line type="monotone" dataKey="uniqueVisitors" stroke="#34d399" strokeWidth={2} dot={false} name="Unique" />
                  <Line type="monotone" dataKey="repeatVisitors" stroke="#fbbf24" strokeWidth={2} dot={false} name="Repeat" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm text-gray-400">No visitor data for this window.</div>
            )}
          </div>
          <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
            <h3 className="text-lg font-semibold text-white mb-2">Daily snapshot</h3>
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {visitorMetrics?.daily.slice().reverse().map(day => (
                <div key={day.day} className="flex items-center justify-between text-sm text-gray-200">
                  <div>
                    <p className="font-medium">{day.day}</p>
                    <p className="text-xs text-gray-400">Repeat: {day.repeatVisitors.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-300">{day.visits.toLocaleString()} visits</p>
                    <p className="text-green-300 text-xs">{day.uniqueVisitors.toLocaleString()} unique</p>
                  </div>
                </div>
              )) || <p className="text-sm text-gray-400">Loading…</p>}
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Scan governance</h2>
            <p className="text-sm text-gray-400">Monitor accepted requests vs blocks/errors across transports.</p>
          </div>
          <div className="flex gap-2">
            {SCAN_WINDOWS.map(window => (
              <button
                key={window}
                className={`px-3 py-1 rounded-md text-sm border ${scanWindow === window ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700 hover:text-white'}`}
                onClick={() => setScanWindow(window)}
              >
                {window}d
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {scanCards.map(card => (
            <div key={card.label} className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">{card.label}</p>
              <p className="text-2xl font-semibold text-white mt-2">{formatNumber(card.value as number | null)}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 rounded-lg border border-gray-700 bg-gray-800/70 p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Scan attempts</h3>
                <p className="text-sm text-gray-400">Accepted vs blocked vs errors</p>
              </div>
              {scanLoading && <span className="text-xs text-blue-300">Refreshing…</span>}
            </div>
            {scanMetrics && scanMetrics.daily.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={scanMetrics.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="day" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151' }} />
                  <Legend />
                  <Bar dataKey="accepted" stackId="a" fill="#34d399" name="Accepted" />
                  <Bar dataKey="blocked" stackId="a" fill="#f87171" name="Blocked" />
                  <Bar dataKey="errors" stackId="a" fill="#fbbf24" name="Errors" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-sm text-gray-400">No scan request activity captured.</div>
            )}
          </div>
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Top block reasons</h3>
              <div className="space-y-2 text-sm text-gray-200">
                {scanMetrics?.totals.topBlockReasons.length
                  ? scanMetrics.totals.topBlockReasons.map(item => (
                    <div key={item.reason} className="flex items-center justify-between">
                      <span>{item.reason}</span>
                      <span className="text-gray-400">{item.count}</span>
                    </div>
                  ))
                  : <p className="text-gray-400">No blocked attempts yet.</p>}
              </div>
            </div>
            <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Transport breakdown</h3>
              <div className="space-y-2 text-sm text-gray-200">
                {transportBreakdown.length
                  ? transportBreakdown.map(item => (
                    <div key={item.via} className="border border-gray-700 rounded-lg p-2">
                      <p className="text-xs uppercase tracking-wide text-gray-400">{item.via.toUpperCase()}</p>
                      <div className="flex justify-between text-xs mt-2">
                        <span className="text-green-300">Accepted</span>
                        <span>{item.accepted}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-red-300">Blocked</span>
                        <span>{item.blocked}</span>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-yellow-300">Errors</span>
                        <span>{item.errors}</span>
                      </div>
                    </div>
                  ))
                  : <p className="text-gray-400">Awaiting telemetry.</p>}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Precision scanning</h2>
            <p className="text-sm text-gray-400">Harness coverage, adaptive payload diversity, and latency medians.</p>
          </div>
          <div className="flex gap-2">
            {PRECISION_WINDOWS.map(window => (
              <button
                key={window}
                className={`px-3 py-1 rounded-md text-sm border ${precisionWindow === window ? 'bg-indigo-600 text-white border-indigo-400' : 'bg-gray-900 text-indigo-200 border-indigo-900 hover:text-white'}`}
                onClick={() => setPrecisionWindow(window)}
              >
                {window}d
              </button>
            ))}
          </div>
        </div>
        {precisionLoading ? (
          <div className="rounded-lg border border-indigo-900/50 bg-indigo-950/30 p-4 text-sm text-indigo-200">
            Loading precision metrics.
          </div>
        ) : precisionMetrics ? (
          <div className="space-y-4">
            {precisionAlerts.length > 0 ? (
              <div className="space-y-2">
                {precisionAlerts.map(alert => {
                  const style = ALERT_STYLES[alert.level]
                  return (
                    <div
                      key={alert.title}
                      className={`rounded-lg border px-4 py-3 text-sm ${style.container}`}
                    >
                      <p className={`font-semibold ${style.accent}`}>{alert.title}</p>
                      <p className="mt-1">{alert.description}</p>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-sm text-emerald-100 px-4 py-3">
                Precision harness and payload lab telemetry meet the healthy thresholds for this window.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {precisionCards.map(card => {
                const tone = (card as any).tone as StatusTone | undefined
                const badge = tone ? STATUS_BADGE_STYLES[tone] : STATUS_BADGE_STYLES.muted
                return (
                  <div key={card.label} className="rounded-lg border border-indigo-900/70 bg-indigo-950/40 p-4 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-indigo-200">{card.label}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-2xl font-semibold text-white">{card.value ?? '-'}</p>
                    {card.helper && <p className="text-xs text-indigo-300 mt-1">{card.helper}</p>}
                  </div>
                )
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-indigo-900/60 bg-indigo-950/30 p-4 text-sm text-indigo-100 space-y-1">
                <h3 className="text-indigo-200 font-semibold mb-1">Stage adoption</h3>
                <div>Pre-scan events: {precisionMetrics.adoption.prescanEvents}</div>
                <div>Harness runs: {precisionMetrics.adoption.harnessUsed}</div>
                <div>Payload batches: {precisionMetrics.adoption.payloadUsed}</div>
                <div>Harness usage: {precisionMetrics.adoption.harnessUsageRate != null ? `${precisionMetrics.adoption.harnessUsageRate.toFixed(1)}%` : '-'}</div>
                <div>Payload usage: {precisionMetrics.adoption.payloadUsageRate != null ? `${precisionMetrics.adoption.payloadUsageRate.toFixed(1)}%` : '-'}</div>
              </div>
              <div className="rounded-lg border border-indigo-900/60 bg-indigo-950/30 p-4 text-sm text-indigo-100 space-y-1">
                <h3 className="text-indigo-200 font-semibold mb-1">Payload diversity</h3>
                <div>Batches executed: {precisionMetrics.payloadDiversity.batches}</div>
                <div>Total variants: {precisionMetrics.payloadDiversity.totalVariants}</div>
                <div>Unique signatures: {precisionMetrics.payloadDiversity.uniqueSignatures}</div>
                <div>Avg variants/batch: {precisionMetrics.payloadDiversity.avgVariantsPerBatch ?? '-'}</div>
                <div>Avg signatures/batch: {precisionMetrics.payloadDiversity.avgSignaturesPerBatch ?? '-'}</div>
              </div>
              <div className="rounded-lg border border-indigo-900/60 bg-indigo-950/30 p-4 text-sm text-indigo-100 space-y-1">
                <h3 className="text-indigo-200 font-semibold mb-1">Latency medians</h3>
                {precisionLatency.length ? precisionLatency.map(entry => (
                  <div key={entry.label} className="flex items-center justify-between">
                    <span>{entry.label}</span>
                    <span>{entry.value != null ? `${entry.value} ms` : '-'}</span>
                  </div>
                )) : <div>No latency telemetry.</div>}
              </div>
            </div>
            <div className="rounded-lg border border-indigo-900/60 bg-indigo-950/30 p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-indigo-100 font-semibold">Trends</h3>
                  <p className="text-xs text-indigo-300/80">Harness runs vs payload batches vs evasions (per day)</p>
                </div>
                {!precisionMetrics.trend.length && <span className="text-xs text-indigo-200">No telemetry</span>}
              </div>
              {precisionMetrics.trend.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={precisionMetrics.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#312e81" />
                    <XAxis dataKey="day" stroke="#a5b4fc" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#a5b4fc" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e1b4b', borderColor: '#312e81' }} />
                    <Legend />
                    <Line type="monotone" dataKey="harnessRuns" stroke="#c4b5fd" strokeWidth={2} dot={false} name="Harness runs" />
                    <Line type="monotone" dataKey="payloadBatches" stroke="#818cf8" strokeWidth={2} dot={false} name="Payload batches" />
                    <Line type="monotone" dataKey="evasions" stroke="#f472b6" strokeWidth={2} dot={false} name="Evasions" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm text-indigo-200">No precision telemetry for this range.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-indigo-900/50 bg-indigo-950/20 p-4 text-sm text-indigo-200">
            No precision telemetry for this window.
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Verification funnel</h2>
            <p className="text-sm text-gray-400">Track ownership checks from request to approval.</p>
          </div>
          <div className="flex gap-2">
            {FUNNEL_WINDOWS.map(window => (
              <button
                key={window}
                className={`px-3 py-1 rounded-md text-sm border ${funnelWindow === window ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700 hover:text-white'}`}
                onClick={() => setFunnelWindow(window)}
              >
                {window}d
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {funnelCards.map(card => (
            <div key={card.label} className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-400">{card.label}</p>
              <p className="text-2xl font-semibold text-white mt-2">{typeof card.value === 'string' ? card.value : formatNumber(card.value as number | null)}</p>
              {card.helper && <p className="text-xs text-gray-500 mt-1">{card.helper}</p>}
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">Requests vs approvals</h3>
              <p className="text-sm text-gray-400">Stacked daily counts</p>
            </div>
            {funnelLoading && <span className="text-xs text-blue-300">Refreshing…</span>}
          </div>
          {funnelMetrics && funnelMetrics.daily.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={funnelMetrics.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="day" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151' }} />
                <Legend />
                <Bar dataKey="requested" stackId="f" fill="#93c5fd" name="Requested" />
                <Bar dataKey="verified" stackId="f" fill="#34d399" name="Verified" />
                <Bar dataKey="failed" stackId="f" fill="#f87171" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-gray-400">No verification telemetry for this window.</div>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">Top entry pages</h2>
            <p className="text-sm text-gray-400">Paths ranked by visit count.</p>
          </div>
          <div className="flex gap-2">
            {TOP_PAGE_WINDOWS.map(window => (
              <button
                key={window}
                className={`px-3 py-1 rounded-md text-sm border ${topPagesWindow === window ? 'bg-blue-600 text-white border-blue-500' : 'bg-gray-800 text-gray-300 border-gray-700 hover:text-white'}`}
                onClick={() => setTopPagesWindow(window)}
              >
                {window}d
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/70">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <div>
              <p className="text-sm text-gray-400">Total visits</p>
              <p className="text-2xl font-semibold text-white">{formatNumber(topPagesMetrics?.totalVisits ?? null)}</p>
            </div>
            {topPagesLoading && <span className="text-xs text-blue-300">Refreshing…</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-gray-400 text-left">
                  <th className="px-4 py-2">Path</th>
                  <th className="px-4 py-2">Visits</th>
                  <th className="px-4 py-2">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800 text-sm text-gray-200">
                {topPagesMetrics?.items.length
                  ? topPagesMetrics.items.map(item => (
                    <tr key={item.path}>
                      <td className="px-4 py-2">{item.path}</td>
                      <td className="px-4 py-2">{item.count.toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-400">{(item.share * 100).toFixed(1)}%</td>
                    </tr>
                  ))
                  : (
                    <tr>
                      <td className="px-4 py-3 text-gray-400" colSpan={3}>
                        {topPagesLoading ? 'Loading top pages…' : 'No visit data for this window.'}
                      </td>
                    </tr>
                  )
                }
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <RunningScansPanel onTerminate={handleTerminateScan} />

      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-blue-200">Site Settings</h2>
          <div className="text-xs text-gray-400">Admin-only</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-gray-300 font-medium">Require Proxy</div>
            <div className="text-sm text-gray-400">Effective: {requireProxyEffective} {settings?.require_proxy.env ? '(ENV override)' : settings?.require_proxy.db ? '(Admin override)' : ''}</div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                disabled={saving}
                className={`px-3 py-1 rounded border ${settings?.require_proxy.effective ? 'bg-green-700 border-green-600' : 'bg-gray-700 border-gray-600'} text-white text-sm w-full sm:w-auto text-center`}
                onClick={async () => {
                  try {
                    setSaving(true)
                    const next = !(settings?.require_proxy.effective)
                    await apiFetch('/api/admin/settings', {
                      method: 'PUT',
                      body: JSON.stringify({ require_proxy: String(next) })
                    })
                    toast.success(`Require Proxy ${next ? 'enabled' : 'disabled'}`)
                    const s = await apiFetch<{ settings: any }>('/api/admin/settings')
                    setSettings(s.settings)
                  } catch (e: any) {
                    toast.error(e.message || 'Failed to update setting')
                  } finally {
                    setSaving(false)
                  }
                }}
              >
                Toggle
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-gray-300 font-medium">Trust Proxy</div>
            <div className="text-sm text-gray-400">Effective: {trustProxyEffective} {settings?.trust_proxy.env ? '(ENV override)' : settings?.trust_proxy.db ? '(Admin override)' : ''}</div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                disabled={saving}
                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-gray-200 w-full sm:w-auto"
                aria-label="Trust Proxy mode"
                value={(settings?.trust_proxy.db ?? settings?.trust_proxy.env ?? 'auto').toString()}
                onChange={e => setSettings(s => s ? { ...s, trust_proxy: { ...s.trust_proxy, db: e.target.value } } : s)}
              >
                <option value="auto">auto (loopback, linklocal, uniquelocal)</option>
                <option value="true">true (trust all)</option>
                <option value="false">false (trust none)</option>
              </select>
              <button
                disabled={saving}
                className="px-3 py-1 rounded border bg-blue-700 border-blue-600 text-white text-sm w-full sm:w-auto text-center"
                onClick={async () => {
                  try {
                    setSaving(true)
                    const val = (settings?.trust_proxy.db ?? 'auto').toString()
                    await apiFetch('/api/admin/settings', {
                      method: 'PUT',
                      body: JSON.stringify({ trust_proxy: val })
                    })
                    toast.success('Trust Proxy updated')
                    const s = await apiFetch<{ settings: any }>('/api/admin/settings')
                    setSettings(s.settings)
                  } catch (e: any) {
                    toast.error(e.message || 'Failed to update trust proxy')
                  } finally {
                    setSaving(false)
                  }
                }}
              >
                Save
              </button>
            </div>
            <div className="text-xs text-gray-500">Tip: When behind a dev proxy (like Vite), set this to auto or true to avoid rate-limit IP warnings.</div>
          </div>
        </div>
      </div>
    </div>
  )
}
